import { useState } from 'react';
import type { Seedling, LocalTrayState, Tending } from '../lib/types';
import { addTending, addAftertaste } from '../lib/store';
import { X, Leaf, Link as LinkIcon, Heart, Camera } from 'lucide-react';

interface Props {
  seedling: Seedling;
  state: LocalTrayState;
  onClose: () => void;
  onChange: () => void;
  onAttach?: (file: File) => void;
}

export function TendingBench({ seedling, state, onClose, onChange, onAttach }: Props) {
  const [mode, setMode] = useState<'menu' | 'witness' | 'graft' | 'press' | 'after'>('menu');
  const [text, setText] = useState('');
  const [attention, setAttention] = useState('');
  const [graftAction, setGraftAction] = useState('');
  const [timeframe, setTimeframe] = useState('7');
  const [bodily, setBodily] = useState('');
  const [recovery, setRecovery] = useState('');
  const [shareAfter, setShareAfter] = useState(false);

  const tendings = state.tendings.filter(t => t.seedling_id === seedling.id);
  const myId = state.myMemberId;
  const isPlanter = seedling.planter_member_id === myId;

  function submit(kind: Tending['kind'], extra?: any) {
    if (!text.trim() && kind !== 'graft') return;
    const payload: any = { kind, content: text.trim() || (kind === 'graft' ? 'I want to do this small thing.' : '') };
    if (extra) Object.assign(payload, extra);
    addTending(seedling.id, kind, payload.content, extra);
    setText('');
    setMode('menu');
    onChange();
  }

  function submitGraft() {
    if (!graftAction.trim()) return;
    const meta = { promised_action: graftAction.trim(), timeframe_days: parseInt(timeframe, 10) || 7 };
    addTending(seedling.id, 'graft', `Graft: ${graftAction}`, meta);
    setGraftAction('');
    setMode('menu');
    onChange();
  }

  function submitAftertaste() {
    if (!bodily.trim() && !recovery.trim()) return;
    addAftertaste(seedling.id, bodily, recovery, shareAfter);
    setBodily(''); setRecovery(''); setShareAfter(false);
    setMode('menu');
    onChange();
  }

  function handleAttach(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f && onAttach) onAttach(f);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 p-3" onClick={onClose}>
      <div
        className="bg-[var(--bg-elev)] border border-[var(--border)] rounded-3xl w-full max-w-[620px] shadow-xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* header */}
        <div className="px-5 py-4 border-b border-[var(--border)] flex items-start justify-between gap-3">
          <div>
            <div className="uppercase tracking-[1.5px] text-[10px] text-[var(--text-subtle)]">seedling in cell {seedling.cell_index + 1}</div>
            <div className="font-medium text-lg leading-tight mt-0.5">{seedling.species}</div>
          </div>
          <button onClick={onClose} className="text-[var(--text-subtle)] hover:text-[var(--text)]"><X size={19} /></button>
        </div>

        <div className="p-5 space-y-5 text-sm">
          {/* the original offering — always visible + captured cognition fields */}
          <div className="bg-[var(--bg)] border border-[var(--border)] rounded-2xl p-4">
            <div className="text-[13px] leading-snug">{seedling.content}</div>
            {seedling.attention_source && (
              <div className="attention mt-2">attention came from: {seedling.attention_source}</div>
            )}
            {isPlanter && (
              <div className="text-[10px] mt-1.5 text-[var(--text-subtle)]">you planted this</div>
            )}

            {(seedling.repair_readiness != null || seedling.intention || seedling.impact || seedling.accountability || (seedling.followups && seedling.followups.length) || (seedling.emotion_tags && seedling.emotion_tags.length)) && (
              <div className="mt-3 pt-3 border-t border-[var(--border)] text-xs space-y-1.5 text-[var(--text-muted)]">
                {seedling.repair_readiness != null && <div>repair readiness: <span className="font-medium text-[var(--text)] tabular-nums">{seedling.repair_readiness}</span></div>}
                {seedling.intention && <div>intention: {seedling.intention}</div>}
                {seedling.impact && <div>impact: {seedling.impact}</div>}
                {seedling.accountability && <div>accountability: {seedling.accountability}</div>}
                {seedling.followups && seedling.followups.length > 0 && (
                  <div>follow-ups: {seedling.followups.map((f) => `${f.actor || 'someone'} — ${f.action} (${f.timeframe})`).join(' • ')}</div>
                )}
                {seedling.emotion_tags && seedling.emotion_tags.length > 0 && <div>textures: {seedling.emotion_tags.join(', ')}</div>}
              </div>
            )}
          </div>

          {/* existing tendings */}
          {tendings.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--text-subtle)] mb-1.5">what has grown here</div>
              <div className="space-y-2">
                {tendings.map(t => {
                  const m = state.members.find(mm => mm.id === t.member_id);
                  const isG = t.kind === 'graft';
                  return (
                    <div key={t.id} className={`rounded-xl px-3 py-2 text-[13px] ${isG ? 'graft' : 'bg-[var(--bg)] border border-[var(--border)]'}`}>
                      <span className="text-[var(--text-subtle)] text-xs">{m?.tendril_name || 'someone'} • {t.kind}</span>
                      <div className="mt-0.5">{t.content}</div>
                      {isG && t.meta_json && (
                        <div className="text-[var(--gold)] text-xs mt-1">— offered with a timeframe</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* actions */}
          {mode === 'menu' && (
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button onClick={() => setMode('witness')} className="flex items-center gap-2 rounded-2xl border px-4 py-3 hover:bg-[var(--bg)] text-left">
                <Leaf size={16} className="text-[var(--moss)]" /> Witness — add one specific thing this called up for you
              </button>
              <button onClick={() => setMode('graft')} className="flex items-center gap-2 rounded-2xl border px-4 py-3 hover:bg-[var(--bg)] text-left">
                <Heart size={16} className="text-[var(--gold)]" /> Graft — one small concrete thing you will actually do
              </button>
              <button onClick={() => setMode('press')} className="flex items-center gap-2 rounded-2xl border px-4 py-3 hover:bg-[var(--bg)] text-left">
                <LinkIcon size={16} className="text-[var(--lavender)]" /> Press into the Spread — your reading of this
              </button>
              <button onClick={() => setMode('after')} className="flex items-center gap-2 rounded-2xl border px-4 py-3 hover:bg-[var(--bg)] text-left">
                Aftertaste — what this left in your body and how long recovery took
              </button>
            </div>
          )}

          {/* witness / press form */}
          {(mode === 'witness' || mode === 'press') && (
            <div className="space-y-3">
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                className="w-full min-h-[92px] rounded-2xl border border-[var(--border)] p-3 bg-transparent text-[14px]"
                placeholder={mode === 'witness' ? 'The light also made me think of how quiet the house got after they moved...' : 'In my reading this seedling is carrying the fear that distance has made us strangers...'}
              />
              <input
                value={attention}
                onChange={e => setAttention(e.target.value)}
                placeholder="where your attention was when you wrote this (optional)"
                className="w-full text-xs rounded-xl border px-3 py-2 bg-transparent"
              />
              <div className="flex gap-2">
                <button onClick={() => { submit(mode === 'witness' ? 'witness' : 'press', attention ? { attention_source: attention } : undefined); }} className="rounded-2xl bg-[var(--accent)] text-white px-5 py-2 text-sm">Add to the tray</button>
                <button onClick={() => setMode('menu')} className="rounded-2xl border px-5 py-2 text-sm">cancel</button>
              </div>
            </div>
          )}

          {/* graft form — the repair move */}
          {mode === 'graft' && (
            <div className="space-y-3">
              <div className="text-xs text-[var(--text-subtle)]">Grafts are specific, small, and time-bounded. No scripted sincerity required.</div>
              <input
                value={graftAction}
                onChange={e => setGraftAction(e.target.value)}
                placeholder="I will send a photo of the actual curtain this week"
                className="w-full rounded-2xl border px-4 py-2.5 text-sm"
              />
              <div className="flex items-center gap-2 text-xs">
                within
                <select value={timeframe} onChange={e => setTimeframe(e.target.value)} className="border rounded px-2 py-1 bg-transparent">
                  {[3,7,10,14,21,30].map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                days
              </div>
              <div className="flex gap-2">
                <button onClick={submitGraft} className="rounded-2xl bg-[var(--gold)] text-[var(--ink)] px-5 py-2 text-sm">Offer this graft</button>
                <button onClick={() => setMode('menu')} className="rounded-2xl border px-5 py-2 text-sm">cancel</button>
              </div>
            </div>
          )}

          {/* aftertaste — emotional + recovery capture */}
          {mode === 'after' && (
            <div className="aftertaste rounded-2xl p-4 space-y-3">
              <div className="text-xs">This stays private to you unless you choose to press it into the spread.</div>
              <textarea value={bodily} onChange={e => setBodily(e.target.value)} placeholder="What was the first small bodily feeling or thought in the hours after?" className="w-full min-h-[64px] rounded-xl border px-3 py-2 text-sm bg-transparent" />
              <textarea value={recovery} onChange={e => setRecovery(e.target.value)} placeholder="Roughly how long before you could return to the tray or the person without it feeling heavy?" className="w-full min-h-[64px] rounded-xl border px-3 py-2 text-sm bg-transparent" />
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={shareAfter} onChange={e => setShareAfter(e.target.checked)} /> share this texture with the circle in the spread
              </label>
              <div className="flex gap-2">
                <button onClick={submitAftertaste} className="rounded-2xl border border-[var(--lavender)] px-5 py-2 text-sm">Record aftertaste</button>
                <button onClick={() => setMode('menu')} className="rounded-2xl border px-5 py-2 text-sm">cancel</button>
              </div>
            </div>
          )}

          {/* photo attach (R2 in live, objectURL in demo) */}
          <div className="pt-2 border-t border-[var(--border)] flex items-center gap-2 text-xs">
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-[var(--text-muted)] hover:text-[var(--text)]">
              <Camera size={14} /> attach a photo or scan (curtain light, note, whatever grounds it)
              <input type="file" accept="image/*" onChange={handleAttach} className="hidden" />
            </label>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[var(--border)] bg-[var(--bg)] text-[10px] text-[var(--text-subtle)]">
          This seedling and everything added to it stays inside this tray. You can retract your own words for a short window.
        </div>
      </div>
    </div>
  );
}
